import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthStore } from '../../../store/authStore';
import { cvApi, emptyCv, cvError, CV_STORAGE_KEY } from './cvApi';
export { CV_STORAGE_KEY } from './cvApi';
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const ordered = value => Array.isArray(value) ? value.map(ordered) : value && typeof value === 'object' ? Object.fromEntries(Object.keys(value).sort().map(key=>[key,ordered(value[key])])) : value;
async function legacyFingerprint(data) {
  const bytes = new TextEncoder().encode(JSON.stringify(ordered(data)));
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), byte=>byte.toString(16).padStart(2,'0')).join('');
}
export function useCvData() {
  const userId = useAuthStore(s => s.user?._id);
  const [context, setContext] = useState({ scope: 'self' });
  const { scope, personId } = context;
  const [persons, setPersons] = useState([]);
  const transition = useRef(false);
  const account = useRef(userId);
  const [profiles, setProfiles] = useState([]);
  const [activeProfileId, setActiveId] = useState(null);
  const [cvData, setData] = useState(emptyCv);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setUnsaved] = useState(false);
  const current = useRef(null),
    draft = useRef(null),
    pending = useRef(null);
  const pendingOperation = useRef(null),
    timer = useRef(null),
    busy = useRef(null);
  const mounted = useRef(true),
    epoch = useRef(0),
    conflict = useRef(false);
  const invalidate = useCallback(() => { ++epoch.current; }, []);
  const draftKey = useCallback(id => scope === 'self' ? `cv_draft_${userId}_${id}` : `cv_draft_${userId}_other_${personId}_${id}`, [userId, scope, personId]);
  const activeKey = scope === 'self' ? `cv_active_${userId}` : `cv_active_${userId}_other_${personId}`;
  const scopedList = useCallback(async () => {
    const listed = await cvApi.list({scope, ...(personId ? {personId} : {})});
    return {...listed, profiles: listed.profiles.filter(p => scope === 'self' ? (p.scope || 'self') === 'self' : p.scope === 'other' && p.personId === personId)};
  }, [scope, personId]);
  const backup = useCallback(() => {
    if (!current.current) return;
    try {
      localStorage.setItem(draftKey(current.current.id), JSON.stringify({
        baseRevision: current.current.revision,
        data: draft.current,
        pending: pending.current
      }));
    } catch {/* The server write still runs if browser backup storage is unavailable. */}
  }, [draftKey]);
  const report = useCallback(e => setError(cvError(e)), []);
  const install = useCallback(profile => {
    if ((profile.scope || 'self') !== scope || (profile.personId || undefined) !== personId) throw new Error('El currículum pertenece a otro contexto. Selecciona la persona correcta.');
    current.current = profile;
    draft.current = profile.data;
    pending.current = null;
    conflict.current = false;
    try {
      const stored = JSON.parse(localStorage.getItem(draftKey(profile.id)) || 'null');
      if (stored) {
        draft.current = stored.data;
        pending.current = stored.pending;
        conflict.current = stored.baseRevision !== profile.revision && !stored.pending;
        setError(conflict.current ? 'El CV cambió mientras había un borrador local. Recarga para conservar la versión de tu cuenta.' : 'Se recuperaron cambios sin guardar. Pulsa Reintentar para guardarlos.');
      } else setError('');
      localStorage.setItem(activeKey, profile.id);
    } catch {
      setError('No se pudo leer el respaldo local. La versión de tu cuenta está disponible.');
    }
    setData(draft.current);
    setUnsaved(!same(profile.data, draft.current) || Boolean(pending.current));
    setActiveId(profile.id);
  }, [draftKey, activeKey, scope, personId]);
  const flush = useCallback(function saveChanges() {
    if (busy.current) return busy.current.then(ok => ok ? saveChanges() : false);
    if (!current.current) return Promise.resolve(true);
    if (conflict.current) return Promise.resolve(false);
    clearTimeout(timer.current);
    setSaving(true);
    const generation = epoch.current;
    const job = (async () => {
      try {
        while (current.current && generation === epoch.current) {
          if (!pending.current) {
            const section = Object.keys(emptyCv()).find(s => !same(current.current.data[s], draft.current[s]));
            if (!section) break;
            const value = JSON.parse(JSON.stringify(draft.current[section]));
            pending.current = {
              action: section,
              input: {
                ...context,
                requestId: crypto.randomUUID(),
                cvId: current.current.id,
                expectedRevision: current.current.revision,
                ...(['personalInfo', 'introduction'].includes(section) ? {
                  changes: value
                } : {
                  operation: 'replace',
                  items: value
                })
              }
            };
            backup();
          }
          const saved = await cvApi.command(pending.current);
          if (generation !== epoch.current) return false;
          current.current = {
            ...current.current,
            revision: saved.revision,
            data: {
              ...current.current.data,
              [saved.section]: saved.data
            },
            entries: {
              ...current.current.entries,
              [saved.section]: saved.entries
            }
          };
          pending.current = null;
          backup();
          setProfiles(prev => prev.map(p => p.id === saved.id ? {
            ...p,
            revision: saved.revision,
            counts: {
              ...p.counts,
              ...(Array.isArray(saved.data) ? {
                [saved.section]: saved.data.length
              } : {})
            }
          } : p));
        }
        if (generation !== epoch.current) return false;
        localStorage.removeItem(draftKey(current.current.id));
        setError('');
        setUnsaved(false);
        return true;
      } catch (e) {
        if (generation === epoch.current) {
          if (e.response?.data?.code === 'CV_CONFLICT') conflict.current = true;
          report(e);
        }
        return false;
      }
    })();
    busy.current = job;
    return job.finally(() => {
      if (busy.current === job) busy.current = null;
      if (mounted.current && generation === epoch.current) setSaving(false);
    });
  }, [backup, draftKey, report, context]);
  useEffect(() => {
    if (account.current !== userId) {
      account.current = userId;
      if (scope !== 'self') { setContext({scope: 'self'}); return; }
    }
    mounted.current = true;
    transition.current = true;
    const generation = ++epoch.current;
    current.current = null;
    draft.current = null;
    pending.current = null;
    pendingOperation.current = null;
    conflict.current = false;
    setProfiles([]);
    setPersons([]);
    setActiveId(null);
    setData(emptyCv());
    setUnsaved(false);
    setLoading(true);
    setSaving(false);
    setError('');
    async function load() {
      if (!userId) {
        setLoading(false);
        transition.current = false;
        return;
      }
      try {
        let listed = await scopedList();
        if (generation !== epoch.current) return;
        const migrationKey = `cv_migrated_${userId}`;
        if (scope === 'self' && !localStorage.getItem(migrationKey)) {
          const raw = localStorage.getItem(CV_STORAGE_KEY);
          if (raw) {
            const parsed = JSON.parse(raw);
            const legacy = Array.isArray(parsed.profiles) ? parsed.profiles : parsed.personalInfo ? [{
              id: 'legacy',
              name: 'CV Principal',
              data: parsed
            }] : [];
            for (const p of legacy) {
              if (generation !== epoch.current) return;
              let legacyId = p.id || 'legacy';
              let name = p.name || 'CV Principal';
              const migrated = listed.profiles.find(v=>v.legacyId===legacyId);
              if (migrated) {
                const remote = await cvApi.get(migrated.id);
                const normalized = {...emptyCv(), ...p.data, personalInfo:{...emptyCv().personalInfo, ...p.data?.personalInfo}};
                if (JSON.stringify(ordered(remote.data)) === JSON.stringify(ordered(normalized))) continue;
                legacyId = legacyId.slice(0,400) + '_local_' + await legacyFingerprint(p.data);
                if (listed.profiles.some(v=>v.legacyId===legacyId)) continue;
                name = name.slice(0,175) + ' (respaldo local)';
              }
              await cvApi.create(name, p.data, {
                legacyId,
                targetRole: p.targetRole || '',
                linkedRouteId: p.linkedRouteId || null,
                language: ['es', 'en', 'pt'].includes(p.language) ? p.language : 'es'
              });
              listed = await scopedList();
            }
            localStorage.setItem(migrationKey, 'done');
          }
        }
        if (scope === 'self' && !listed.profiles.length) {
          // Deterministic legacyId prevents duplicate starter CVs in StrictMode or concurrent tabs.
          await cvApi.create('CV Principal', emptyCv(), {
            legacyId: '__starter__'
          });
          listed = await scopedList();
        }
        if (generation !== epoch.current) return;
        setProfiles(listed.profiles);
        setPersons(listed.persons || []);
        const preferred = localStorage.getItem(activeKey);
        const id = listed.profiles.find(p => p.id === preferred)?.id || listed.profiles[0]?.id;
        if (!id) return;
        const profile = await cvApi.get(id, context);
        if (generation === epoch.current) install(profile);
      } catch (e) {
        if (generation === epoch.current) report(e);
      } finally {
        if (generation === epoch.current) { setLoading(false); transition.current = false; }
      }
    }
    load();
    return () => {
      mounted.current = false;
      invalidate();
      clearTimeout(timer.current);
    };
  }, [userId, install, report, scopedList, scope, activeKey, context, invalidate]);
  const updateCvData = useCallback(value => {
    if (!current.current || transition.current || pendingOperation.current) return;
    draft.current = typeof value === 'function' ? value(draft.current) : value;
    setData(draft.current);
    setUnsaved(true);
    backup();
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 600);
  }, [backup, flush]);
  const setActiveProfile = useCallback(async id => {
    if (transition.current || pendingOperation.current || !profiles.some(p => p.id === id)) return false;
    const generation = epoch.current;
    transition.current = true;
    if (!(await flush())) { if (generation === epoch.current) transition.current = false; return false; }
    if (generation !== epoch.current) return false;
    setLoading(true);
    try {
      const profile = await cvApi.get(id, context);
      if (generation !== epoch.current) return false;
      install(profile);
      return true;
    } catch (e) {
      if (generation === epoch.current) report(e);
      return false;
    } finally {
      if (generation === epoch.current) { setLoading(false); transition.current = false; }
    }
  }, [flush, install, report, context, profiles]);
  const changeContext = useCallback(async next => {
    if (transition.current) return false;
    if (pendingOperation.current) { setError('Hay una operación pendiente. Pulsa Reintentar antes de continuar.'); return false; }
    const generation = epoch.current;
    transition.current = true;
    if (!await flush()) { if (generation === epoch.current) transition.current = false; return false; }
    if (generation !== epoch.current) return false;
    ++epoch.current;
    current.current = null; draft.current = null; pending.current = null; pendingOperation.current = null;
    setProfiles([]); setActiveId(null); setData(emptyCv()); setError(''); setUnsaved(false); setLoading(true);
    setContext(next);
    return true;
  }, [flush]);
  const perform = useCallback(async command => {
    if (transition.current) return null;
    if (pendingOperation.current && command.requestId !== pendingOperation.current.requestId) { setError('Hay una operación pendiente. Pulsa Reintentar antes de continuar.'); return null; }
    const generation = epoch.current;
    transition.current = true;
    if (!(await flush())) { if (generation === epoch.current) transition.current = false; return null; }
    if (generation !== epoch.current) return null;
    const contextual = {...context, ...command};
    const prepared = ['delete','meta'].includes(command.action) && current.current?.id === command.cvId ? {...contextual,expectedRevision:current.current.revision} : contextual;
    pendingOperation.current = pendingOperation.current || prepared;
    setLoading(true);
    try {
      const result = await cvApi.command(pendingOperation.current);
      if (generation !== epoch.current) return null;
      const destination = result.importedIds ? {scope: result.scope, ...(result.personId ? {personId: result.personId} : {})} : command.personName ? {scope: 'other', personId: result.personId} : context;
      if (destination.scope !== scope || destination.personId !== personId) {
        const key = destination.scope === 'self' ? `cv_active_${userId}` : `cv_active_${userId}_other_${destination.personId}`;
        try { localStorage.setItem(key, result.importedIds?.[0] || result.id); } catch { /* The server remains authoritative. */ }
        pendingOperation.current = null;
        transition.current = false;
        await changeContext(destination);
        return result.importedIds?.[0] || result.id;
      }
      const listed = await scopedList();
      if (generation !== epoch.current) return null;
      setProfiles(listed.profiles);
      setPersons(listed.persons || []);
      if (result.deleted) {
        localStorage.removeItem(draftKey(result.id));
        if (listed.profiles.length) {
          const profile = await cvApi.get(listed.profiles[0].id, context);
          if (generation === epoch.current) install(profile);
        } else {
          current.current = null;
          draft.current = emptyCv();
          setData(emptyCv());
          setActiveId(null);
          setUnsaved(false);
          localStorage.removeItem(activeKey);
        }
      } else if (result.importedIds) {
        const profile = await cvApi.get(result.importedIds[0], context);
        if (generation !== epoch.current) return null;
        install(profile);
      } else install(result);
      pendingOperation.current = null;
      return result.importedIds?.[0] || result.id;
    } catch (e) {
      if (generation === epoch.current) {
        if (e.response?.status >= 400 && e.response?.status < 500) pendingOperation.current = null;
        report(e);
      }
      return null;
    } finally {
      if (generation === epoch.current) { setLoading(false); transition.current = false; }
    }
  }, [draftKey, flush, install, report, context, scopedList, changeContext, activeKey, scope, personId, userId]);
  const exportProfiles = useCallback(async ids => {
    if (transition.current || !ids.length || ids.some(id => !profiles.some(p => p.id === id))) return null;
    if (pendingOperation.current) { setError('Hay una operación pendiente. Pulsa Reintentar antes de continuar.'); return null; }
    const generation = epoch.current;
    transition.current = true;
    if (!await flush()) { if (generation === epoch.current) transition.current = false; return null; }
    if (generation !== epoch.current) return null;
    setLoading(true);
    try {
      const documents = await Promise.all([...new Set(ids)].map(id => cvApi.get(id, context)));
      if (generation !== epoch.current) return null;
      if (documents.some(p => (p.scope || 'self') !== scope || (p.personId || undefined) !== personId)) throw new Error('El currículum pertenece a otro contexto. Selecciona la persona correcta.');
      return documents;
    } catch (e) { if (generation === epoch.current) report(e); return null; }
    finally { if (generation === epoch.current) { setLoading(false); transition.current = false; } }
  }, [context, flush, profiles, scope, personId, report]);
  const refresh = useCallback(async (discard = false) => {
    if (transition.current) return;
    if (pendingOperation.current) { setError('Hay una operación pendiente. Pulsa Reintentar antes de continuar.'); return; }
    const generation = epoch.current;
    transition.current = true;
    if (discard && current.current) {
      clearTimeout(timer.current); localStorage.removeItem(draftKey(current.current.id));
      pending.current = null; pendingOperation.current = null; conflict.current = false; draft.current = current.current.data;
    }
    if (!await flush()) { if (generation === epoch.current) transition.current = false; return; }
    if (generation !== epoch.current) return;
    setLoading(true);
    try {
      const listed = await scopedList();
      if (generation !== epoch.current) return;
      setProfiles(listed.profiles);
      setPersons(listed.persons || []);
      const id = listed.profiles.find(p => p.id === activeProfileId)?.id || listed.profiles[0]?.id;
      if (id) { const profile = await cvApi.get(id, context); if (generation === epoch.current) install(profile); }
      else { current.current = null; draft.current = emptyCv(); setData(emptyCv()); setActiveId(null); setUnsaved(false); setError(''); }
    } catch(e) { if (generation === epoch.current) report(e); }
    finally { if (generation === epoch.current) { setLoading(false); transition.current = false; } }
  }, [activeProfileId, draftKey, flush, install, report, scopedList, context]);
  useEffect(() => {
    const pull = () => {
      if (!userId || loading || document.visibilityState === 'hidden' || busy.current || pendingOperation.current || pending.current || (current.current && !same(current.current.data, draft.current))) return;
      refresh();
    };
    window.addEventListener('focus', pull);
    return () => window.removeEventListener('focus', pull);
  }, [loading, refresh, userId]);
  useEffect(() => {
    const unload = e => {
      if (pendingOperation.current || pending.current || (current.current && !same(current.current.data, draft.current))) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', unload);
    return () => window.removeEventListener('beforeunload', unload);
  }, []);
  return {
    context,
    persons,
    activePerson: persons.find(p => p.id === personId),
    changeContext,
    exportProfiles,
    importProfiles: payload => perform({action: 'import', ...payload, personId: payload.scope === 'other' ? payload.personId : undefined}),
    createNewPerson: name => perform({action: 'create', requestId: crypto.randomUUID(), scope: 'other', personId: undefined, personName: name, name: 'CV Principal'}),
    cvData,
    updateCvData,
    profiles,
    activeProfileId,
    activeProfile: profiles.find(p => p.id === activeProfileId),
    loading,
    hasPendingOperation: Boolean(pendingOperation.current),
    error,
    saving,
    hasUnsavedChanges,
    flush,
    setActiveProfile,
    refresh,
    retry: () => pendingOperation.current ? perform(pendingOperation.current) : current.current ? flush() : refresh(),
    createNewProfile: (name, targetRole = '', linkedRouteId = null, language = 'es') => perform({
      action: 'create',
      requestId: crypto.randomUUID(),
      name,
      targetRole,
      linkedRouteId,
      language
    }),
    duplicateProfile: id => perform({
      action: 'duplicate',
      requestId: crypto.randomUUID(),
      cvId: id
    }),
    deleteProfile: id => perform({
      action: 'delete',
      requestId: crypto.randomUUID(),
      cvId: id,
      expectedRevision: profiles.find(p => p.id === id).revision
    }),
    updateProfileMeta: (id, changes) => perform({
      action: 'meta',
      requestId: crypto.randomUUID(),
      cvId: id,
      expectedRevision: profiles.find(p => p.id === id).revision,
      changes
    })
  };
}
